import React from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from '@material-ui/core';

type Option<V = string> = {
  value: V;
  label: React.ReactNode;
  disabled?: boolean;
};

export type FormCheckboxesProps<V = string> = {
  name: string;
  control: Control<any>;
  label?: React.ReactNode;
  options: Option<V>[];
  helperText?: React.ReactNode;
  disabled?: boolean;
  formControlProps?: React.ComponentProps<typeof FormControl>;
  className?: string;
  columns?: number;
};

function stringKey(v: unknown) {
  return String(v);
}

const FormCheckboxes = React.forwardRef<HTMLDivElement, FormCheckboxesProps<any>>(
  (
    {
      name,
      control,
      label,
      options,
      helperText,
      disabled,
      formControlProps,
      className,
      columns = 2,
    },
    ref,
  ) => {
    const ariaLabelId = label ? `${name}-label` : undefined;

    const valueMap = React.useMemo(() => {
      const m = new Map<string, any>();
      options.forEach((o) => m.set(stringKey(o.value), o.value));
      return m;
    }, [options]);

    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          const isDisabled = disabled || field.disabled;

          const currentKeys = Array.isArray(field.value)
            ? (field.value as any[]).map(stringKey)
            : [];

          const toggle = (key: string) => {
            const set = new Set(currentKeys);
            if (set.has(key)) set.delete(key);
            else set.add(key);

            const nextValues = Array.from(set).map((k) => valueMap.get(k) ?? k);
            field.onChange(nextValues);
          };

          return (
            <FormControl
              error={hasError}
              disabled={isDisabled}
              className={className}
              {...formControlProps}
            >
              {label && <FormLabel id={ariaLabelId}>{label}</FormLabel>}

              <FormGroup
                ref={ref}
                row={false}
                aria-labelledby={ariaLabelId}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                  columnGap: 16,
                  rowGap: 4,
                }}
              >
                {options.map((opt) => {
                  const key = stringKey(opt.value);
                  const checked = currentKeys.includes(key);

                  return (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => toggle(key)}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      }
                      label={opt.label}
                      disabled={opt.disabled}
                      style={{ marginRight: 0 }}
                    />
                  );
                })}
              </FormGroup>

              {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
            </FormControl>
          );
        }}
      />
    );
  },
);

FormCheckboxes.displayName = 'FormCheckboxes';
export default FormCheckboxes;
